import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { moneySchema, quantitySchema, timestampSchema, uuidSchema } from "../common/primitives.js";
import { ASSET_STATUSES, WASH_UNIT_TYPES } from "../masterdata/assets.js";
import { productUnitSchema } from "../masterdata/products.js";
import { washProgramKindSchema, washStartModeSchema, washUnitStateSchema } from "./enums.js";
/**
 * Yıkama TANIMLARI (Prompt 18, ADR 0018 §4–6): kiracı ayarı, ünite ayarı (`wash_units` ile 1:1), program + reçete,
 * ünite ↔ program ve kimyasal stoğu görünümü. İzinler: okuma `wash.read`, yazma `wash.manage`; bayrak `WASH`.
 */
const versionSchema = z.number().int().min(0).describe("İyimser kilit sürümü (kayıt yoksa 0)");
const nonNegativeQuantity = quantitySchema.refine((value) => !value.startsWith("-"), "Miktar negatif olamaz");
const positiveQuantity = quantitySchema.refine((value) => Number(value) > 0, "Miktar sıfırdan büyük olmalıdır");
const TIME = /^([01]\d|2[0-3]):([0-5]\d)$/;
// ── Kiracı ayarı ───────────────────────────────────────────────────────────────
export const washSettingsSchema = z.object({
    codeValidityMinutes: z.number().int(),
    creditValidityDays: z.number().int(),
    codeAttemptLimit: z.number().int(),
    photoRetentionDays: z.number().int(),
    readySmsTemplate: z.string(),
    /** Kayıt yoksa varsayılanlar döner (`version: 0`). */
    isDefault: z.boolean(),
    version: z.number().int(),
    updatedAt: timestampSchema.nullable(),
    canEdit: z
        .boolean()
        .describe("Çağıran ayarı yazabilir mi — sunucu hesaplar: KİRACI kapsamlı `wash.manage` (ayar tüm istasyonları etkiler)"),
});
/** PUT — ayar TAMAMEN yazılır (kısmi güncelleme değil; `save…` adlandırması: PATCH şeması kuralı dışında). */
export const saveWashSettingsSchema = z.object({
    codeValidityMinutes: z.number().int().min(5).max(43_200).describe("Başlatma kodunun geçerliliği (dk)"),
    creditValidityDays: z.number().int().min(1).max(3650).describe("Self-servis kredisinin geçerliliği (gün)"),
    codeAttemptLimit: z.number().int().min(1).max(20).describe("Ünite başına dakikada hatalı kod denemesi"),
    photoRetentionDays: z
        .number()
        .int()
        .min(1)
        .max(3650)
        .describe("İş emri fotoğraflarının saklama süresi (gün)"),
    readySmsTemplate: z
        .string()
        .trim()
        .min(10)
        .max(300)
        .describe("İşlemsel 'aracınız hazır' metni; yer tutucular {plaka} {istasyon} {isEmriNo}"),
    version: versionSchema,
});
// ── Ünite ──────────────────────────────────────────────────────────────────────
export const washUnitIdParamsSchema = z.object({ unitId: uuidSchema });
export const washOpeningWindowInputSchema = z
    .object({
    weekdays: z
        .array(z.number().int().min(1).max(7))
        .max(7)
        .default([])
        .refine((days) => new Set(days).size === days.length, "Aynı gün iki kez seçilemez")
        .describe("ISO hafta günleri (1 = Pazartesi … 7 = Pazar); boş = her gün"),
    from: z.string().regex(TIME, "Saat HH:MM olmalı").describe("İstanbul saati"),
    to: z.string().regex(TIME, "Saat HH:MM olmalı").describe("İstanbul saati (hariç)"),
})
    .refine((window) => window.from < window.to, {
    message: "Bitiş saati başlangıçtan sonra olmalı",
    path: ["to"],
});
export const washUnitSettingsSchema = z.object({
    deviceRef: z.string().nullable().describe("Ajan adaptöründeki bölme / makine başvurusu (ör. BAY-1)"),
    startMode: washStartModeSchema,
    capacity: z.number().int(),
    slotMinutes: z.number().int(),
    openingHours: z.array(z.object({ weekdays: z.array(z.number().int()), from: z.string(), to: z.string() })),
    waterMeterId: uuidSchema.nullable(),
    waterMeterName: z.string().nullable().describe("Seçili su alt sayacının adı (gider modülünün sayacı)"),
    electricityMeterId: uuidSchema.nullable(),
    electricityMeterName: z.string().nullable().describe("Seçili elektrik alt sayacının adı"),
    /** Ayar kaydı yoksa varsayılan (`MANUAL`, kapasite 1) — `version: 0`. */
    isDefault: z.boolean(),
    version: z.number().int(),
});
export const washUnitProgramSchema = z.object({
    programId: uuidSchema,
    code: z.string(),
    name: z.string(),
    kind: washProgramKindSchema,
    productId: uuidSchema,
    durationSeconds: z.number().int().nullable(),
    deviceProgramCode: z.string().nullable(),
    isActive: z.boolean(),
});
/**
 * Sürmekte olan çalışma: ünitenin AÇIK `RUNNING` durum aralığından türetilir (ajan `WashUnitStatus`). Ünitede aralığın
 * başlangıcından en çok 5 dakika önce ya da ondan sonra başlamış (`STARTED`) bir başlatma varsa program ve beklenen bitiş ondan
 * gelir (en yenisi); makinenin kendi tahsilatıyla / krediyle başlayan ya da eşleşmeyen çalışmada program ve bitiş null'dır
 * (ekran "çalışıyor" + geçen süre gösterir).
 */
export const washUnitCurrentCycleSchema = z.object({
    startedAt: timestampSchema.describe("Açık RUNNING aralığının başlangıcı"),
    activationId: uuidSchema.nullable(),
    programId: uuidSchema.nullable(),
    programCode: z.string().nullable(),
    programName: z.string().nullable(),
    expectedEndAt: timestampSchema
        .nullable()
        .describe("Başlangıç + programın tahmini süresi; program ya da süre bilinmiyorsa null"),
});
export const washUnitLiveSchema = z.object({
    /** Son bilinen durum (ajan `WashUnitStatus`); hiç durum gelmediyse null. */
    state: washUnitStateSchema.nullable(),
    faultCode: z.string().nullable(),
    since: timestampSchema.nullable(),
    /** Bugün (İstanbul) biten çalışma sayısı. */
    todayCycles: z.number().int(),
    lastCycleAt: timestampSchema.nullable(),
    /** Ünite şu an çalışıyorsa (açık RUNNING aralığı) sürmekte olan çalışma; değilse null. */
    currentCycle: washUnitCurrentCycleSchema.nullable(),
});
export const washUnitViewSchema = z.object({
    id: uuidSchema,
    stationId: uuidSchema,
    unitType: z.enum(WASH_UNIT_TYPES),
    name: z.string(),
    status: z.enum(ASSET_STATUSES),
    settings: washUnitSettingsSchema,
    programs: z.array(washUnitProgramSchema),
    live: washUnitLiveSchema,
});
export const listWashUnitsQuerySchema = z.object({ stationId: uuidSchema });
export const washUnitListSchema = z.object({ items: z.array(washUnitViewSchema) });
/** PUT — ünite ayarı TAMAMEN yazılır (verilmeyen alan varsayılana döner). */
export const saveWashUnitSettingsSchema = z.object({
    deviceRef: z
        .string()
        .trim()
        .min(1)
        .max(60)
        .nullable()
        .default(null)
        .describe("REMOTE / CODE ünitede zorunlu; aynı istasyonda tekil"),
    startMode: washStartModeSchema,
    capacity: z.number().int().min(1).max(20).default(1),
    slotMinutes: z.number().int().min(5).max(240).default(30),
    openingHours: z.array(washOpeningWindowInputSchema).max(21).default([]),
    waterMeterId: uuidSchema
        .nullable()
        .default(null)
        .describe("Ünitenin su alt sayacı (aynı istasyonun WATER sayacı)"),
    electricityMeterId: uuidSchema
        .nullable()
        .default(null)
        .describe("Ünitenin elektrik alt sayacı (aynı istasyonun ELECTRICITY sayacı)"),
    version: versionSchema,
});
export const putWashUnitProgramsSchema = z.object({
    programs: z
        .array(z.object({
        programId: uuidSchema,
        deviceProgramCode: z
            .string()
            .trim()
            .min(1)
            .max(60)
            .nullable()
            .default(null)
            .describe("Makinedeki program kodu (REMOTE / CODE ünitede başlatma bununla yapılır)"),
    }))
        .max(100)
        .refine((items) => new Set(items.map((item) => item.programId)).size === items.length, "Aynı program iki kez eklenemez")
        .refine((items) => {
        const codes = items.map((item) => item.deviceProgramCode).filter((code) => code !== null);
        return new Set(codes).size === codes.length;
    }, "Makine program kodu ünitede tekil olmalı"),
});
// ── Program ve reçete ─────────────────────────────────────────────────────────
export const washProgramConsumptionSchema = z.object({
    productId: uuidSchema,
    productCode: z.string(),
    productName: z.string(),
    unit: productUnitSchema,
    accountingGroup: z.string(),
    quantity: z.string().describe("Çalışma başına (self-serviste DAKİKA başına), ürün biriminde"),
});
export const washProgramSchema = z.object({
    id: uuidSchema,
    code: z.string(),
    name: z.string(),
    kind: washProgramKindSchema,
    productId: uuidSchema.describe("Satış ürünü — fiyat ürünün istasyon fiyatıdır (tek fiyat kaynağı)"),
    productCode: z.string(),
    productName: z.string(),
    productUnit: productUnitSchema,
    durationSeconds: z.number().int().nullable(),
    waterLitres: z.string().describe("Çalışma başına (self-serviste dakika başına) tahmini su (L)"),
    electricityKwh: z.string().describe("Çalışma başına (self-serviste dakika başına) tahmini elektrik (kWh)"),
    isActive: z.boolean(),
    consumptions: z.array(washProgramConsumptionSchema),
    unitIds: z.array(uuidSchema).describe("Programın geçerli olduğu üniteler"),
    currentPrice: moneySchema
        .nullable()
        .describe("`stationId` verildiyse satış ürününün o istasyondaki GÜNCEL fiyatı (KDV dahil, kuruş; iş emri satırıyla aynı kural). Tahmin içindir — iş emri / check-in fiyatı sunucuda sabitler. Fiyat tanımsızsa ya da istasyon verilmediyse null."),
    version: z.number().int(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
export const washProgramPageSchema = pageOf(washProgramSchema);
export const listWashProgramsQuerySchema = pageQuerySchema.extend({
    stationId: uuidSchema
        .optional()
        .describe("Verilirse her programın bu istasyondaki güncel fiyatı (`currentPrice`) döner — `wash.read` yeterli"),
    kind: washProgramKindSchema.optional(),
    isActive: z
        .enum(["true", "false"])
        .transform((value) => value === "true")
        .optional(),
});
const programFields = {
    name: z.string().trim().min(1, "Program adı zorunludur").max(120),
    productId: uuidSchema.describe("İş birimi WASH, türü WASH_SERVICE ya da SERVICE; self-serviste birimi MINUTE"),
    durationSeconds: z
        .number()
        .int()
        .min(30)
        .max(86_400)
        .nullable()
        .default(null)
        .describe("Tahmini süre (sn); self-servis dışında zorunlu"),
    waterLitres: nonNegativeQuantity.default("0"),
    electricityKwh: nonNegativeQuantity.default("0"),
    isActive: z.boolean().default(true),
};
export const washRecipeLineInputSchema = z.object({
    productId: uuidSchema.describe("Kimyasal — ürün türü CONSUMABLE"),
    quantity: positiveQuantity,
});
const recipeLines = z
    .array(washRecipeLineInputSchema)
    .max(50)
    .refine((lines) => new Set(lines.map((line) => line.productId)).size === lines.length, "Aynı kimyasal iki satırda olamaz");
export const createWashProgramSchema = z
    .object({
    code: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z0-9][A-Z0-9_-]{0,29}$/, "Kod 1–30 karakter; büyük harf, rakam, - ve _"),
    kind: washProgramKindSchema,
    ...programFields,
    consumptions: recipeLines.default([]),
})
    .refine((value) => value.kind === "SELF_SERVICE" || value.durationSeconds !== null, {
    message: "Self-servis dışındaki programda süre zorunludur",
    path: ["durationSeconds"],
});
export const updateWashProgramSchema = z.object({
    name: programFields.name.optional(),
    productId: uuidSchema.optional(),
    durationSeconds: z.number().int().min(30).max(86_400).nullable().optional(),
    waterLitres: nonNegativeQuantity.optional(),
    electricityKwh: nonNegativeQuantity.optional(),
    isActive: z.boolean().optional(),
    version: z.number().int().min(1),
});
export const putWashProgramConsumptionsSchema = z.object({
    lines: recipeLines,
    version: z.number().int().min(1),
});
// ── Alt sayaç seçenekleri (ünite ayarı) ───────────────────────────────────────
export const washMetersQuerySchema = z.object({ stationId: uuidSchema });
/** İstasyonun ETKİN su / elektrik sayaçları (gider modülünün salt-okuma portu) — ünite ayarındaki sayaç seçimi. */
export const washMeterOptionSchema = z.object({
    id: uuidSchema,
    name: z.string(),
    meterType: z.enum(["WATER", "ELECTRICITY"]),
    businessUnitType: z.string().nullable().describe("Sayacın iş birimi (WASH = yıkama alt sayacı)"),
    parentMeterId: uuidSchema.nullable(),
    parentName: z.string().nullable().describe("Üst sayacın adı (alt sayaçsa)"),
});
export const washMeterListSchema = z.object({ items: z.array(washMeterOptionSchema) });
// ── Kimyasal stoğu ─────────────────────────────────────────────────────────────
export const washChemicalsQuerySchema = z.object({ stationId: uuidSchema });
export const washChemicalSchema = z.object({
    productId: uuidSchema,
    code: z.string(),
    name: z.string(),
    unit: productUnitSchema,
    accountingGroup: z.string(),
    /** Yıkama deposundaki bakiye (MARKET kapalıysa null — stok tutulmaz). */
    quantity: z.string().nullable(),
    value: moneySchema.nullable(),
    averageUnitCost: z.string().nullable().describe("₺, 4 ondalık (değer / miktar)"),
    consumedLastPeriod: z.string().describe("Son `periodDays` günün tüketimi (stok defteri ya da teorik)"),
    dailyAverage: z.string(),
    daysOfCover: z.string().nullable(),
    usedInPrograms: z.array(z.string()).describe("Reçetesinde bulunduğu program kodları"),
});
export const washChemicalsSchema = z.object({
    stationId: uuidSchema,
    warehouseId: uuidSchema.nullable(),
    /** MARKET istasyonda açık mı: kapalıysa stok tutulmaz, tüketim reçeteden TEORİK hesaplanır. */
    stockTracked: z.boolean(),
    periodDays: z.number().int(),
    items: z.array(washChemicalSchema),
    warnings: z.array(z.string()),
});
//# sourceMappingURL=definitions.js.map